import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AppNav from '../../components/app/AppNav';
import { apiGet, apiPost, apiUploadImage } from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import { CITIES } from '../../constants/cities';
import { NATIONALITIES } from '../../constants/nationalities';

interface ProfileData {
  name?: string;
  about?: string;
  city?: string;
  nationality?: string;
  display_pic?: string;
  warning_count?: number;
}

export default function AppSettingsProfile() {
  const navigate = useNavigate();
  const { userProfile, refreshAuth } = useAuth();

  const [name, setName] = useState('');
  const [about, setAbout] = useState('');
  const [city, setCity] = useState('');
  const [nationality, setNationality] = useState('Indian');
  const [avatar, setAvatar] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [email, setEmail] = useState('');
  const [warningCount, setWarningCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // City search dropdown
  const [citySearch, setCitySearch] = useState('');
  const [cityOpen, setCityOpen] = useState(false);
  const [onlineCities, setOnlineCities] = useState<{ name: string; country: string }[]>([]);
  const [searchingCities, setSearchingCities] = useState(false);
  const cityRef = useRef<HTMLDivElement>(null);

  // Nationality dropdown
  const [natSearch, setNatSearch] = useState('');
  const [natOpen, setNatOpen] = useState(false);
  const natRef = useRef<HTMLDivElement>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const community = nationality && city ? `${nationality} in ${city}` : '';

  useEffect(() => {
    apiGet<{ data: ProfileData }>('/api_profile', undefined, true)
      .then(res => {
        const d = res.data;
        setName(d.name || '');
        setAbout(d.about || '');
        setCity(d.city || '');
        setCitySearch(d.city || '');
        setNationality(d.nationality || 'Indian');
        setNatSearch(d.nationality || 'Indian');
        setAvatar(d.display_pic || userProfile?.picture || null);
        setWarningCount(d.warning_count || 0);
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : '';
        // 404 = new user with no profile yet — just show empty form
        if (!msg.includes('404')) setError('Failed to load profile');
      })
      .finally(() => setLoading(false));

    setEmail(userProfile?.email || '');
  }, [userProfile]);

  // Auto-detect city via GPS for new users (city is empty after profile loads)
  useEffect(() => {
    if (loading || city) return;
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json`,
          { headers: { 'User-Agent': 'Sahayy/1.0.0' } }
        );
        const data = await res.json();
        const detected = data.address?.city || data.address?.town || data.address?.village || data.address?.county || '';
        if (detected) {
          setCity(detected);
          setCitySearch(detected);
        }
      } catch { /* silent */ }
    }, () => { /* GPS denied — silent */ });
  }, [loading]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (cityRef.current && !cityRef.current.contains(e.target as Node)) setCityOpen(false);
      if (natRef.current && !natRef.current.contains(e.target as Node)) setNatOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const localCities = CITIES.filter(c => c.toLowerCase().includes(citySearch.toLowerCase())).slice(0, 40);
  const onlineOnly = onlineCities.filter(o => !localCities.includes(o.name));
  const allCities = [
    ...localCities.map(name => ({ name, country: '' })),
    ...onlineOnly,
  ];

  // Nominatim search (debounced, mirrors mobile CityPicker)
  useEffect(() => {
    if (citySearch.length < 3) { setOnlineCities([]); return; }
    const t = setTimeout(async () => {
      setSearchingCities(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(citySearch)}&format=json&addressdetails=1&limit=10&featuretype=city`,
          { headers: { 'User-Agent': 'Sahayy/1.0.0' } },
        );
        if (!res.ok) return;
        const text = await res.text();
        if (!text || text.startsWith('<')) return;
        const data = JSON.parse(text);
        if (!Array.isArray(data)) return;
        setOnlineCities(
          data
            .filter((item: any) => (item.importance || 0) >= 0.4)
            .map((item: any) => ({
              name: item.display_name.split(',')[0],
              country: item.address?.country || '',
            }))
            .slice(0, 10),
        );
      } catch { /* silent */ } finally {
        setSearchingCities(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [citySearch]);
  const filteredNats = NATIONALITIES.filter(n => n.toLowerCase().includes(natSearch.toLowerCase())).slice(0, 60);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatar(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      let display_pic = avatar;
      if (avatarFile) {
        display_pic = await apiUploadImage(avatarFile);
      }
      await apiPost('/api_save_profile', {
        name: name.trim(),
        about: about.trim(),
        city,
        nationality,
        ...(display_pic && { display_pic }),
      }, true);
      setSaved(true);
      await refreshAuth();
      setTimeout(() => { setSaved(false); navigate('/settings'); }, 1200);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <AppNav />
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 64px)' }}>
        <div style={{ width: '32px', height: '32px', border: '3px solid #f0f0f0', borderTop: '3px solid #FF385C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '0.75rem 1rem', border: '1.5px solid #e5e7eb',
    borderRadius: '10px', fontSize: '0.95rem', color: '#111',
    backgroundColor: '#fff', outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <AppNav />

      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '2rem 1rem 6rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <button onClick={() => navigate('/settings')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#111', fontWeight: 600, fontSize: '0.9rem', padding: 0 }}>← Settings</button>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111', margin: 0, flex: 1 }}>Edit Profile</h1>
        </div>

        {/* Avatar */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '2rem' }}>
          <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => fileInputRef.current?.click()}>
            {avatar ? (
              <img src={avatar} alt="avatar" style={{ width: '90px', height: '90px', borderRadius: '50%', objectFit: 'cover' }} />
            ) : (
              <div style={{
                width: '90px', height: '90px', borderRadius: '50%',
                backgroundColor: '#FF385C', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '2.2rem', fontWeight: 700,
              }}>
                {name.charAt(0).toUpperCase() || '?'}
              </div>
            )}
            <div style={{
              position: 'absolute', bottom: 0, right: 0,
              width: '28px', height: '28px', borderRadius: '50%',
              backgroundColor: '#FF385C', border: '2px solid #fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ color: '#fff', fontSize: '0.75rem' }}>✏️</span>
            </div>
          </div>
          <p style={{ fontSize: '0.82rem', color: '#9ca3af', marginTop: '0.5rem' }}>Profile Picture</p>
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
        </div>

        <div style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

          {/* Name */}
          <div>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>Name *</label>
            <input
              style={inputStyle}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Enter your name"
              onFocus={e => { e.currentTarget.style.borderColor = '#FF385C'; }}
              onBlur={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
            />
          </div>

          {/* Email (read-only) */}
          <div>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>Email</label>
            <input
              style={{ ...inputStyle, color: '#9ca3af', backgroundColor: '#f9fafb', cursor: 'not-allowed' }}
              value={email}
              readOnly
            />
            <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.25rem 0 0' }}>Email is managed by your sign-in provider</p>
          </div>

          {/* About */}
          <div>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>About</label>
            <textarea
              style={{ ...inputStyle, minHeight: '100px', resize: 'vertical', fontFamily: 'inherit' }}
              value={about}
              onChange={e => setAbout(e.target.value)}
              placeholder="Tell us about yourself"
              onFocus={e => { e.currentTarget.style.borderColor = '#FF385C'; }}
              onBlur={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
            />
          </div>

          {/* City dropdown */}
          <div ref={cityRef}>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>City</label>
            <div style={{ position: 'relative' }}>
              <input
                style={inputStyle}
                value={citySearch}
                onChange={e => { setCitySearch(e.target.value); setCityOpen(true); if (!e.target.value) setCity(''); }}
                onFocus={() => setCityOpen(true)}
                placeholder="Search city..."
              />
              {cityOpen && (allCities.length > 0 || searchingCities) && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                  backgroundColor: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '10px',
                  maxHeight: '260px', overflowY: 'auto', zIndex: 100,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                }}>
                  {searchingCities && (
                    <div style={{ padding: '0.65rem 1rem', fontSize: '0.82rem', color: '#9ca3af', fontStyle: 'italic' }}>
                      Searching cities...
                    </div>
                  )}
                  {allCities.map((c, i) => (
                    <button key={`${c.name}-${i}`} onClick={() => { setCity(c.name); setCitySearch(c.name); setCityOpen(false); setOnlineCities([]); }}
                      style={{
                        display: 'flex', flexDirection: 'column', width: '100%', textAlign: 'left',
                        padding: '0.65rem 1rem', background: city === c.name ? '#fff5f5' : 'none',
                        border: 'none', borderTop: i > 0 ? '1px solid #f3f4f6' : 'none',
                        cursor: 'pointer', fontSize: '0.9rem',
                        color: city === c.name ? '#FF385C' : '#111', fontWeight: city === c.name ? 600 : 400,
                      }}
                      onMouseEnter={e => { if (city !== c.name) (e.currentTarget as HTMLButtonElement).style.background = '#f9fafb'; }}
                      onMouseLeave={e => { if (city !== c.name) (e.currentTarget as HTMLButtonElement).style.background = city === c.name ? '#fff5f5' : 'none'; }}
                    >
                      {c.name}
                      {c.country && <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 400 }}>{c.country}</span>}
                    </button>
                  ))}
                  {!searchingCities && allCities.length === 0 && citySearch.length > 0 && (
                    <div style={{ padding: '0.65rem 1rem', fontSize: '0.82rem', color: '#9ca3af' }}>No cities found</div>
                  )}
                  {citySearch.length === 0 && (
                    <div style={{ padding: '0.65rem 1rem', fontSize: '0.82rem', color: '#9ca3af', fontStyle: 'italic' }}>Type to search cities worldwide</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Nationality dropdown */}
          <div ref={natRef}>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>Nationality</label>
            <div style={{ position: 'relative' }}>
              <input
                style={inputStyle}
                value={natSearch}
                onChange={e => { setNatSearch(e.target.value); setNatOpen(true); }}
                onFocus={() => setNatOpen(true)}
                placeholder="Search nationality..."
              />
              {natOpen && filteredNats.length > 0 && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                  backgroundColor: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '10px',
                  maxHeight: '220px', overflowY: 'auto', zIndex: 100,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                }}>
                  {filteredNats.map(n => (
                    <button key={n} onClick={() => { setNationality(n); setNatSearch(n); setNatOpen(false); }}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '0.65rem 1rem', background: nationality === n ? '#fff5f5' : 'none',
                        border: 'none', cursor: 'pointer', fontSize: '0.9rem',
                        color: nationality === n ? '#FF385C' : '#111', fontWeight: nationality === n ? 600 : 400,
                      }}
                      onMouseEnter={e => { if (nationality !== n) (e.currentTarget as HTMLButtonElement).style.background = '#f9fafb'; }}
                      onMouseLeave={e => { if (nationality !== n) (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Community preview */}
          {community && (
            <div style={{ backgroundColor: '#f9fafb', borderRadius: '10px', padding: '1rem', border: '1px solid #e5e7eb' }}>
              <p style={{ margin: '0 0 0.25rem', fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>Your Community</p>
              <p style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 700, color: '#FF385C' }}>{community}</p>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#6b7280', fontStyle: 'italic' }}>
                You will see posts in <strong>{community}</strong> and city-wide posts in <strong>Global in {community.split(' in ')[1]}</strong>.
              </p>
            </div>
          )}

          <p style={{ margin: 0, fontSize: '0.78rem', color: '#9ca3af', fontStyle: 'italic', textAlign: 'center' }}>
            Your city and nationality determine your community.
          </p>
        </div>

        {/* Warning banner */}
        {warningCount > 0 && (
          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '10px', border: '1px solid #ffeaa7' }}>
            <p style={{ margin: '0 0 0.25rem', fontWeight: 600, color: '#856404', fontSize: '0.9rem' }}>⚠️ Account Warning</p>
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#856404' }}>
              You have received {warningCount} warning{warningCount > 1 ? 's' : ''} due to community reports. Please review our community guidelines.
            </p>
          </div>
        )}

        {error && (
          <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', backgroundColor: '#fee2e2', borderRadius: '10px', color: '#dc2626', fontSize: '0.875rem' }}>
            {error}
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={saving || saved}
          style={{
            display: 'block', width: '100%', marginTop: '1.5rem',
            padding: '0.875rem', borderRadius: '10px', border: 'none',
            background: saved ? '#16a34a' : 'linear-gradient(135deg,#FF385C,#e11d48)',
            color: '#fff', fontSize: '0.95rem', fontWeight: 600, cursor: saving || saved ? 'default' : 'pointer',
            opacity: saving ? 0.7 : 1, transition: 'background 0.2s',
            boxShadow: '0 4px 14px rgba(255,56,92,0.3)',
          }}
        >
          {saved ? '✓ Profile Saved!' : saving ? 'Saving...' : 'Save Profile'}
        </button>
      </div>
    </div>
  );
}
